import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Field, FieldLabel, Input } from "@akuna/ui";
import { X } from "lucide-react";
import { nightsBetween } from "@/lib/daycare-hotel/stayDays";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";
import { formatDateBR } from "@/lib/date-utils";

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

export interface PackageDatesFieldProps {
  module: StayPlanModule;
  includedDays: number;
  value: string[];
  onChange: (dates: string[]) => void;
  disabled?: boolean;
}

/**
 * As datas que o pacote já compra. Creche marca dias soltos; hotel marca uma
 * estadia e cada pernoite consome uma diária. Escolher menos que o total é o
 * caso normal: o que sobra vira saldo, sem prazo.
 */
export function PackageDatesField({ module, includedDays, value, onChange, disabled = false }: PackageDatesFieldProps) {
  const { t } = useTranslation("daycareHotel");
  const [day, setDay] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [error, setError] = useState<string | null>(null);

  const remaining = Math.max(0, includedDays - value.length);

  const addDay = () => {
    if (disabled || !ISO_DATE.test(day) || value.includes(day) || remaining === 0) return;
    onChange([...value, day].sort());
    setDay("");
  };

  const addRange = () => {
    if (disabled || !ISO_DATE.test(from) || !ISO_DATE.test(to)) return;
    const nights = nightsBetween(from, to).filter((date) => !value.includes(date));
    if (nights.length === 0) return;
    if (nights.length > remaining) {
      setError(t("packageDates.tooManyNights", { count: remaining }));
      return;
    }
    setError(null);
    onChange([...value, ...nights].sort());
    setFrom("");
    setTo("");
  };

  return (
    <div className="flex flex-col gap-3">
      {module === "hotel" ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field className="flex-1">
            <FieldLabel htmlFor="package-from">{t("packageDates.fromLabel")}</FieldLabel>
            <Input
              id="package-from"
              type="date"
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              disabled={disabled}
            />
          </Field>
          <Field className="flex-1">
            <FieldLabel htmlFor="package-to">{t("packageDates.toLabel")}</FieldLabel>
            <Input
              id="package-to"
              type="date"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              disabled={disabled}
            />
          </Field>
          <Button type="button" variant="outline" onClick={addRange} disabled={disabled || remaining === 0}>
            {t("packageDates.addRange")}
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-2">
          <Field className="flex-1">
            <FieldLabel htmlFor="package-day">{t("packageDates.addDayLabel")}</FieldLabel>
            <Input
              id="package-day"
              type="date"
              value={day}
              onChange={(event) => setDay(event.target.value)}
              disabled={disabled}
            />
          </Field>
          <Button type="button" variant="outline" onClick={addDay} disabled={disabled || remaining === 0}>
            {t("packageDates.addDay")}
          </Button>
        </div>
      )}

      {error ? (
        <p role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}

      {value.length > 0 ? (
        <ul className="flex flex-wrap gap-1.5">
          {value.map((date) => (
            <li key={date}>
              <span className="inline-flex items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                {formatDateBR(date)}
                <button
                  type="button"
                  aria-label={t("packageDates.remove")}
                  disabled={disabled}
                  onClick={() => onChange(value.filter((current) => current !== date))}
                  className="rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <X aria-hidden="true" className="h-3 w-3" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      <p role="status" className="text-xs text-muted-foreground">
        {t("packageDates.remaining", { count: remaining })}
      </p>
    </div>
  );
}
